/* eslint-disable @typescript-eslint/consistent-type-assertions */
/* eslint-disable @octopusdeploy/custom-portal-rules/no-restricted-imports */
import type { PaletteType, Theme as MuiTheme } from "@material-ui/core";
import { useTheme as useMuiTheme } from "@material-ui/core/styles";
import type { ThemeOptions as MuiThemeOptions } from "@material-ui/core/styles/createMuiTheme";
import muiCreateMuiTheme from "@material-ui/core/styles/createMuiTheme";
import type { Palette as MuiPalette, PaletteOptions as MuiPaletteOptions } from "@material-ui/core/styles/createPalette";
import { colorScales } from "@octopusdeploy/design-system-tokens";
import type { OctopusTheme } from "~/components/Theme";
import { createLegacyMuiTheme } from "./legacy";
interface StatusColors {
    danger: string;
    info: string;
}
interface ExtraPaletteOptions {
    status: StatusColors;
}
export type OctopusPaletteOptions = ExtraPaletteOptions & MuiPaletteOptions;
function createLightPalette(theme: OctopusTheme): OctopusPaletteOptions {
    return {
        type: "light",
        primary: { main: colorScales.blue["500"] },
        secondary: {
            main: colorScales.blue["500"],
            contrastText: colorScales.white,
        },
        text: {
            // The most important text.
            primary: theme.primaryText,
            // Secondary text.
            secondary: theme.secondaryText,
            // Disabled text have even lower visual prominence.
            disabled: theme.disabledButtonText,
            // Text hints.
            hint: theme.ternaryText,
        },
        background: {
            paper: theme.paper1,
            default: theme.paper1,
        },
        divider: theme.dividerLight,
        // The colors used to style the action elements.
        action: {
            // The color of an active action like an icon button.
            active: theme.primaryText,
            // The color of an hovered action.
            hover: theme.hover,
            hoverOpacity: 0.08,
            // The color of a selected action.
            selected: theme.hover,
            // The color of a disabled action.
            disabled: theme.disabledButtonText,
            // The background color of a disabled action.
            disabledBackground: theme.disabledBackground,
        },
        error: {
            main: theme.danger,
        },
        status: {
            danger: theme.alert,
            info: theme.info,
        },
    };
}
function createDarkPalette(theme: OctopusTheme): OctopusPaletteOptions {
    return {
        type: "dark",
        primary: { main: colorScales.blue["400"] },
        secondary: {
            main: colorScales.blue["400"],
        },
        background: {
            paper: theme.paper1,
            default: theme.paper1,
        },
        text: {
            // The most important text.
            primary: theme.primaryText,
            // Secondary text.
            secondary: theme.secondaryText,
            // Disabled text have even lower visual prominence.
            disabled: theme.disabledButtonText,
            // Text hints.
            hint: theme.ternaryText,
        },
        divider: theme.dividerLight,
        error: {
            main: theme.danger,
        },
        status: {
            danger: theme.alert,
            info: theme.info,
        },
    };
}
export const zIndexDialogs = 900;
export const zIndexPopovers = 800;
export const zIndexStickyTopPanel = 11;
export const zIndexStickies = 10;
function createMuiTheme(type: PaletteType, octopusTheme: OctopusTheme): MuiTheme {
    const palette = type === "dark" ? createDarkPalette(octopusTheme) : createLightPalette(octopusTheme);
    const options: MuiThemeOptions = {
        palette,
        overrides: {
            MuiMenu: {
                paper: {
                    backgroundColor: octopusTheme.paper2,
                },
            },
            MuiDivider: {
                root: {
                    backgroundColor: octopusTheme.teritaryDivider,
                },
            },
            MuiPopover: {
                paper: {
                    boxShadow: octopusTheme.shadowMedium,
                },
            },
            MuiDialog: {
                paper: {
                    boxShadow: octopusTheme.shadowLarge,
                },
            },
        },
    };
    return muiCreateMuiTheme(options) as Theme;
}
type ThemePaletteType = PaletteType;
interface OctopusPalette extends MuiPalette {
    status: StatusColors;
}
export interface Theme extends MuiTheme {
    palette: OctopusPalette;
}
export const useTheme = () => {
    return useMuiTheme<Theme>();
};
export default createMuiTheme;
export { createMuiTheme, ThemePaletteType, createLegacyMuiTheme };
